import { ComponentType, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { useReportV2Store } from '@/frontend/store/ReportV2Store';
import { ReportAssetsProvider, ReportEditingProvider } from '@/frontend/reports/primitives';
import { ReportRenderBoundary } from '@/frontend/reports/ReportRenderBoundary';
import { ReportViewProps } from '@/frontend/reports/types';
import { useReportModule } from '@/frontend/reports/useReportModule';
import { ReportV2Version } from '@/backend/models/reportV2Version';
import { InputsReportContext, ReportEvaluation, ReportValues, asInputsReport } from '@/reports/types';
import { LoadingSpinner } from '../LoadingSpinner';
import { VerdictBadge } from '../runReports/ReportBadges';
import { buttonVariants } from '../ui/button';
import { SaveStatus, later, queueState, useSince } from './ReportV2FillPage';

type DocumentView = ComponentType<ReportViewProps<ReportValues, ReportEvaluation, InputsReportContext>>;
const AUTOSAVE_MS = 700;

function Shell({ crumbs, side, children }: { crumbs: ReactNode; side?: ReactNode; children: ReactNode }) {
  return (
    <div data-slot="reports-v2" className="min-h-svh bg-muted/40 text-sm text-foreground">
      <header className="flex flex-wrap items-center justify-between gap-[0.75rem] border-b-[1px] bg-background px-[1.5rem] py-[0.75rem]">
        <nav aria-label={I18n.get('breadcrumb')} className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">{crumbs}</nav>
        <div className="flex items-center gap-2">{side}</div>
      </header>
      {children}
    </div>
  );
}

/** In place of a report whose own code threw while drawing it: the page around it stays usable. */
function RenderFailed({ message }: { message: string }) {
  return (
    <div role="alert" className="mx-[1.5rem] flex flex-col gap-1 rounded-lg border-[1px] border-destructive/40 bg-background p-[1rem] text-destructive">
      <p className="font-medium">{I18n.get('rv2ReportRenderFailed')}</p>
      <p className="font-mono text-xs">{message}</p>
    </div>
  );
}

/**
 * The report as it prints, from what was entered so far. What the report
 * itself asks — remarks, the sign-offs — is filled in here, on the document,
 * and saves itself. Publishing happens on the fill page.
 */
export function ReportV2PreviewPage() {
  const { id = '' } = useParams();
  const state = useReportV2Store((s) => s.states[id]);
  const missing = useReportV2Store((s) => !!s.missing[id]);
  const fetchState = useReportV2Store((s) => s.fetchState);
  const flushRecord = useReportV2Store((s) => s.flushRecord);
  // what is typed on the record and not saved yet, from the fill page
  const queue = useReportV2Store((s) => (s.states[id] ? s.queues[s.states[id].record.id] : undefined));
  const save = useReportV2Store((s) => s.save);
  const fileUrls = useReportV2Store((s) => s.fileUrls);
  const [values, setValues] = useState<ReportValues | null>(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef<ReportValues | null>(null);
  const timer = useRef<number | null>(null);
  // reading the report for the first time, after what was typed on its record saved
  const reading = useRef(false);

  useEffect(() => {
    setValues(null);
    // always read again: the inputs were probably just changed on the fill page, and what was typed there last is saved first
    const recordId = useReportV2Store.getState().states[id]?.record.id;
    reading.current = true;
    void (async () => {
      if (recordId) await flushRecord(recordId);
      await fetchState(id);
      reading.current = false;
    })();
  }, [id, fetchState, flushRecord]);

  // a save of the record that lands later (tried again, say) changes what the report reads: read it again once everything is saved
  const recordState = queueState(queue);
  const lastState = useRef(recordState);
  useEffect(() => {
    if (!reading.current && lastState.current !== 'saved' && recordState === 'saved') void fetchState(id);
    lastState.current = recordState;
  }, [recordState, id, fetchState]);

  const { module, error } = useReportModule(state ? { key: state.report.reportKey, bundleVersionId: state.bundle.versionId } : null);
  const definition = module?.definition ? asInputsReport(module.definition) : null;
  useEffect(() => {
    if (definition && state && values === null) setValues(definition.normalizeValues(state.report.values));
  }, [definition, state, values]);

  const flush = useCallback(async () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    const body = pending.current;
    if (!body) return;
    pending.current = null;
    setSaving(true);
    try {
      await save(id, { values: body });
    } finally {
      setSaving(false);
    }
  }, [id, save]);
  useEffect(() => () => { void flush(); }, [flush]);

  const onChange = (next: ReportValues) => {
    setValues(next);
    pending.current = next;
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), AUTOSAVE_MS);
  };

  const recordId = state?.record.id ?? null;
  const resolveFiles = useCallback((ids: string[]) => (recordId ? fileUrls(recordId, ids) : Promise.resolve({})), [fileUrls, recordId]);
  const model = useMemo(() => {
    if (!definition || !values || !state) return null;
    try {
      return { model: definition.evaluate(values, state.context), error: null };
    } catch (e) {
      return { model: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [definition, values, state]);
  const since = useSince(later(state?.report.updatedAt, state?.record.updatedAt));

  if (missing && !state) return <Shell crumbs={<Link to="/reports-v2">{I18n.get('rv2Reports')}</Link>}><p className="p-[1.5rem] text-muted-foreground">{I18n.get('modelNotFound', { model: I18n.get('reportV2') })}</p></Shell>;
  if (!state) return <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;

  const crumbs = (
    <>
      <Link to="/reports-v2" className="hover:text-foreground">{I18n.get('rv2Reports')}</Link>
      <ChevronRight className="size-3" />
      <Link to={`/reports-v2/${id}`} className="hover:text-foreground">{state.report.title}</Link>
      <ChevronRight className="size-3" />
      <span className="text-sky-800">{I18n.get('rv2Preview')}</span>
    </>
  );
  const side = (
    <>
      {model?.model && <VerdictBadge verdict={model.model.verdict} label={model.model.verdictLabel} />}
      <span className="text-xs text-muted-foreground" aria-live="polite">{saving ? I18n.get('saving') : <SaveStatus queue={queue} sheet={state.sheet} since={since} />}</span>
      <Link to={`/reports-v2/${id}`} className={buttonVariants({ variant: 'outline' })} onClick={() => void flush()}><ArrowLeft /> {I18n.get('rv2BackToInputs')}</Link>
    </>
  );
  if (error || model?.error) return <Shell crumbs={crumbs} side={side}><p className="p-[1.5rem] text-destructive">{I18n.get('reportBundleLoadFailed', { message: error ?? model?.error ?? '' })}</p></Shell>;
  if (!module || !definition || !values || !model?.model) return <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;

  const Document = module.components.Document as unknown as DocumentView;
  return (
    <Shell crumbs={crumbs} side={side}>
      {/* the report keeps its own styling, as everywhere it prints */}
      <div data-slot="report-preview" className="mx-auto max-w-[64rem] py-[1.25rem]">
        <ReportAssetsProvider resolve={resolveFiles}>
          <ReportEditingProvider readOnly={false}>
            <ReportRenderBoundary resetKey={state.bundle.versionId} fallback={(message) => <RenderFailed message={message} />}>
              <Document values={values} model={model.model} context={state.context} meta={{ reportVersion: definition.version }} onChange={onChange} />
            </ReportRenderBoundary>
          </ReportEditingProvider>
        </ReportAssetsProvider>
      </div>
    </Shell>
  );
}

/** A published version: exactly what was frozen, read-only. */
export function ReportV2VersionPage() {
  const { id = '', versionId = '' } = useParams();
  const fetchVersion = useReportV2Store((s) => s.fetchVersion);
  const fileUrls = useReportV2Store((s) => s.fileUrls);
  const [version, setVersion] = useState<ReportV2Version | null | undefined>(undefined);
  useEffect(() => {
    void fetchVersion(id, versionId).then(setVersion);
  }, [id, versionId, fetchVersion]);
  const { module, error } = useReportModule(version ? { key: version.reportKey, bundleVersionId: version.reportBundleVersionId } : null);
  // the files are the record's: the one it was judged on
  const recordId = version?.recordId ?? null;
  const resolveFiles = useCallback((ids: string[]) => (recordId ? fileUrls(recordId, ids) : Promise.resolve({})), [fileUrls, recordId]);

  const crumbs = (
    <>
      <Link to="/reports-v2" className="hover:text-foreground">{I18n.get('rv2Reports')}</Link>
      <ChevronRight className="size-3" />
      <Link to={`/reports-v2/${id}`} className="hover:text-foreground">{version?.title ?? '…'}</Link>
      <ChevronRight className="size-3" />
      <span className="text-sky-800">{version ? I18n.get('rv2VersionNumber', { number: String(version.number) }) : ''}</span>
    </>
  );
  if (version === null) return <Shell crumbs={crumbs}><p className="p-[1.5rem] text-muted-foreground">{I18n.get('modelNotFound', { model: I18n.get('reportV2Version') })}</p></Shell>;
  if (error) return <Shell crumbs={crumbs}><p className="p-[1.5rem] text-destructive">{I18n.get('reportBundleLoadFailed', { message: error })}</p></Shell>;
  if (!version || !module) return <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;

  const Document = module.components.Document as unknown as DocumentView;
  const model: ReportEvaluation = { verdict: version.verdict, verdictLabel: version.verdictLabel, findings: version.findings };
  return (
    <Shell crumbs={crumbs} side={<span className="text-xs text-muted-foreground">{I18n.get('rv2PublishedBy', { by: version.publishedBy, when: new Date(version.createdAt ?? '').toLocaleString() })}</span>}>
      <div data-slot="report-preview" className="mx-auto max-w-[64rem] py-[1.25rem]">
        <ReportAssetsProvider resolve={resolveFiles}>
          <ReportEditingProvider readOnly>
            <ReportRenderBoundary resetKey={version.id} fallback={(message) => <RenderFailed message={message} />}>
              <Document values={version.values} model={model} context={version.context} meta={{ reportVersion: version.reportVersion, versionNumber: version.number, publishedAt: String(version.createdAt ?? ''), publishedBy: version.publishedBy }} />
            </ReportRenderBoundary>
          </ReportEditingProvider>
        </ReportAssetsProvider>
      </div>
    </Shell>
  );
}
